import { ReactNode } from 'react'
import { DocumentInterface } from '@/services/folders/types'
import { resolveDocumentMongoId } from '@/utils/mongo-id'

/** Active folder in the Finder — use for create/import actions. */
export interface FinderCreateContext {
    currentFolderId: string | null
    currentFolder?: DocumentInterface
}

export type FinderCreateSlot = ReactNode | ((ctx: FinderCreateContext) => ReactNode)

// Extend DocumentInterface for Finder-specific needs
export interface FinderItem extends DocumentInterface {
    // Computed fields for easier access
    type: 'file' | 'folder'
    dataType?: 'document' | 'image' | 'video' | 'pdf' | 'spreadsheet' | 'presentation' | 'audio'
    extension?: string
    itemCount?: number
    tags?: string[]
    thumbnail?: string
}

export type ViewMode = 'grid' | 'list'
export type SortField = 'name' | 'updatedAt' | 'createdAt' | 'last_opened' | 'size'
export type SortDirection = 'asc' | 'desc'

export interface SortConfig {
    field: SortField
    direction: SortDirection
}

export interface BreadcrumbItem {
    id: string | null
    name: string
}

// Convert DocumentInterface to FinderItem
function toFinderItem(doc: DocumentInterface): FinderItem {
    const id = resolveDocumentMongoId((doc as { id?: string }).id ?? doc._id) ?? ''
    return {
        ...doc,
        id,
        type: doc.isFolder ? 'folder' : 'file',
        dataType: mapDataType(doc.data_type),
        extension: getExtension(doc),
        // children_count is computed server-side by getDocumentsPipeline.
        itemCount: (doc as any).children_count,
        tags: doc.tag ? [doc.tag] : undefined,
        thumbnail: doc.url || undefined,
    }
}

// Convert array of DocumentInterface to FinderItem[]
export function toFinderItems(docs: DocumentInterface[]): FinderItem[] {
    return docs.map(toFinderItem)
}

function mapDataType(dataType: DocumentInterface['data_type']): FinderItem['dataType'] {
    switch (dataType) {
        case 'info':
            return 'document'
        case 'image':
            return 'image'
        case 'video':
            return 'video'
        case 'pdf':
            return 'pdf'
        default:
            return 'document'
    }
}

function getExtension(doc: DocumentInterface): string | undefined {
    if (doc.isFolder) return undefined
    switch (doc.data_type) {
        case 'pdf':
            return 'PDF'
        case 'image':
            return doc.type?.toUpperCase() || 'IMG'
        case 'video':
            return 'MP4'
        default:
            return 'DOC'
    }
}
